<template>
  <fullscreen-dialog
    v-model="model"
    :title="`${toLatin(employee?.name || '')} ${toLatin(employee?.lastname || '')}, ID ${
      employee?.id || ''
    }`"
    :subtitle="toLatin(company?.company || '')"
    hide-footer
  >
    <template #default>
      <v-card-text class="py-4">
        <v-row align="center">
          <v-col>
            <month-picker
              v-model="date"
              @updatedMonthDates="monthDates = $event"
              color="white"
            />
          </v-col>
          <v-col cols="auto">
            <text-field
              v-model="search"
              kind="search"
              :label="$t('search')"
              style="width: 250px"
              color="white"
            />
          </v-col>
          <v-col cols="auto">
            <v-checkbox
              class="show-all-checkbox mt-4"
              :label="$t('show_all_days')"
              v-model="showAllDates"
              dense
            />
          </v-col>
          <v-col cols="auto">
            <dropdown
              :label="$t('view')"
              v-model="selectedLayout"
              :items="layouts"
              color="white"
              style="width: 280px"
            />
          </v-col>
          <v-col cols="auto">
            <print-button @click="print" />
          </v-col>
        </v-row>
        <v-data-table
          :headers="headers"
          :items="showAllDates ? ordersByDatesEntireMonth : ordersByDates"
          :search="search"
          :rows-per-page="[100]"
          :items-per-page="100"
          hide-default-footer
          class="ordera-box-shadow striped-table"
        >
          <template v-slot:item="{ item }">
            <tr>
              <td>{{ item.date }}</td>
              <td>{{ item.shift }}</td>
              <template v-for="category in mealCategories">
                <td>
                  <span v-if="Array.isArray(item[category]) && item[category].length">
                    <template v-for="(meal, mealIndex) in item[category]">
                      <template v-if="selectedLayout <= 1">
                        <strong v-if="selectedLayout === 0"
                          >{{ meal.quantity }}x&nbsp;</strong
                        >
                        <v-tooltip bottom v-if="meal.label !== $t('total')">
                          <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">{{ toLatin(meal.name) }}</span>
                          </template>
                          <span>{{ meal.id }}</span>
                        </v-tooltip>
                        <span v-else>{{ meal.label }}</span>
                      </template>
                      <strong v-else-if="selectedLayout === 2">{{
                        meal.quantity
                      }}</strong>
                      <template v-else>{{ meal.label }}</template>
                      <br v-if="mealIndex < item[category].length - 1" />
                    </template>
                  </span>
                </td>
              </template>
              <td>{{ item.total || "" }}</td>
              <td>{{ item.totalOrders || "" }}</td>
              <td>
                {{ item.total && item.totalOrders ? item.total * item.totalOrders : "" }}
              </td>
            </tr>
          </template>
          <template v-slot:body.append>
            <tr>
              <td style="font-weight: bold" colspan="2">{{ $t("total") }}</td>
              <template v-for="category in mealCategories">
                <td style="font-weight: bold">{{ totalByCategory[category] }}</td>
              </template>
              <td style="font-weight: bold">{{ totalQuantity }}</td>
              <td style="font-weight: bold">
                {{ ordersByDates.reduce((sum, row) => sum + row.totalOrders, 0) }}
              </td>
              <td style="font-weight: bold">{{ totalOrders }}</td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </template>
  </fullscreen-dialog>
</template>

<script>
import reportService from "@/services/report";
import hasModel from "@/mixins/hasModel";
import hasPrinting from "@/mixins/hasPrinting";
import hasFoodOrders from "@/mixins/report/hasFoodOrders";
import hasSnackbar from "@/mixins/hasSnackbar";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";

export default {
  name: "ReportEmployeeMealOrderHistoryDialog",
  mixins: [hasModel, hasPrinting, hasFoodOrders, hasSnackbar],
  components: { FullscreenDialog },
  props: {
    employee: {
      type: Object,
      default: () => ({}),
    },
    company: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      date: new Date().toISOString().substr(0, 7),
      search: "",
      ordersByDates: [],
      monthDates: [],
      showAllDates: false,
      selectedLayout: 0,
    };
  },
  created() {
    const dateTo = this.filters.dates.to;
    if (dateTo) {
      this.date = new Date(dateTo).toISOString().substr(0, 7);
    }
  },
  computed: {
    layouts() {
      return [
        { text: this.$t("quantities_and_names"), value: 0 },
        { text: this.$t("names_only"), value: 1 },
        { text: this.$t("quantities_only"), value: 2 },
        { text: this.$t("spare_meals"), value: 3 },
      ];
    },
    ordersByDatesEntireMonth() {
      const allMonthDateEntries = [];
      for (const currentDateStr of this.monthDates) {
        const existingOrderDay = this.ordersByDates.find(
          (order) => order.dateFull === currentDateStr
        );
        if (existingOrderDay) {
          allMonthDateEntries.push(existingOrderDay);
        } else {
          const defaultEntry = {
            date: new Date(currentDateStr).getDate() + ".",
            shift: null,
            total: 0,
            totalOrders: 0,
            dateFull: currentDateStr,
          };
          this.mealCategories.forEach((category) => {
            defaultEntry[category] = [];
          });
          allMonthDateEntries.push(defaultEntry);
        }
      }
      return allMonthDateEntries;
    },
    filters() {
      return this.$store.state.report.filters;
    },
    month() {
      return parseInt(this.date.split("-")[1]);
    },
    year() {
      return parseInt(this.date.split("-")[0]);
    },
    monthStart() {
      return `${this.date}-01`;
    },
    monthEnd() {
      const lastDay = new Date(this.year, this.month, 0).getDate();
      return `${this.date}-${String(lastDay).padStart(2, "0")}`;
    },
    headers() {
      const items = [
        { text: this.$t("date"), value: "date" },
        { text: this.$t("shift.shift"), value: "shift" },
        ...this.mealCategories.map((category) => ({
          text: this.toLatin(category),
          value: category || "",
        })),
        { text: this.$t("number_of_foods"), value: "total" },
        { text: this.$t("number_of_orders"), value: "totalOrders" },
        { text: this.$t("total"), value: "rowTotal" },
      ];
      return items;
    },
    printItems() {
      const items = this.showAllDates
        ? this.ordersByDatesEntireMonth
        : this.ordersByDates;
      return items.map((item) => {
        const newItem = { ...item };
        this.mealCategories.forEach((category) => {
          newItem[category] = this.formatMealsForPrint(item[category]);
        });
        newItem.rowTotal =
          item.total && item.totalOrders ? item.total * item.totalOrders : "";
        return newItem;
      });
    },
    printTotalRow() {
      const totalRow = { date: this.$t("total"), shift: "" };
      this.mealCategories.forEach((category) => {
        totalRow[category] = this.totalByCategory[category];
      });
      totalRow.total = this.totalQuantity;
      totalRow.totalOrders = this.ordersByDates.reduce(
        (sum, row) => sum + row.totalOrders,
        0
      );
      totalRow.rowTotal = this.totalOrders;
      return totalRow;
    },
    totalByCategory() {
      const totals = {};
      this.mealCategories.forEach((category) => {
        totals[category] = this.ordersByDates.reduce((sum, row) => {
          const arr = row[category];
          if (Array.isArray(arr)) {
            return sum + arr.reduce((catSum, meal) => catSum + (meal.quantity || 0), 0);
          }
          return sum;
        }, 0);
      });
      return totals;
    },
    totalQuantity() {
      return this.ordersByDates.reduce((sum, row) => sum + (parseInt(row.total) || 0), 0);
    },
    totalOrders() {
      return this.ordersByDates.reduce(
        (sum, row) => sum + (parseInt(row.total) || 0) * (parseInt(row.totalOrders) || 0),
        0
      );
    },
  },
  methods: {
    formatMealsForPrint(meals) {
      if (!Array.isArray(meals)) return "";
      switch (this.selectedLayout) {
        case 0:
          return meals.map((m) => `${m.quantity}x ${this.toLatin(m.name)}`).join(", ");
        case 1:
          return meals.map((m) => this.toLatin(m.name)).join(", ");
        case 2:
          return meals.map((m) => m.quantity).join(", ");
        default:
          return meals.map((m) => m.label).join(", ");
      }
    },
    print() {
      this.printDocument({
        tables: [
          { headers: this.headers, items: [...this.printItems, this.printTotalRow] },
        ],
        title: `${this.toLatin(this.employee.name)} ${this.toLatin(
          this.employee.lastname
        )}`,
        subtitle: [this.monthDates[0], this.monthDates.slice(-1)[0]]
          .map((date) => this.localeDate(date))
          .join(" - "),
      });
    },
    async loadReport() {
      if (!this.employee?.id) return;
      this.loading = true;
      this.ordersByDates = [];
      try {
        const response = await reportService.getEmployeeOrdersByMonth({
          month: this.month,
          year: this.year,
          date_from: this.monthStart,
          date_to: this.monthEnd,
          user_id: this.employee.id,
        });
        const data = response.data;
        const dates = [...new Set(data.map((item) => item.date))];

        // Response nema category_name - grupišemo po restaurant_name
        this.mealCategories = [
          ...new Set(data.map((item) => item.restaurant_name).filter(Boolean)),
        ];

        this.ordersByDates = dates.map((date) => {
          const dayOrders = data.filter((item) => item.date === date);
          const dailyOrders = {
            date: new Date(date).getDate() + ".",
            shift: dayOrders[0]?.shift,
            restaurant_name: dayOrders[0]?.restaurant_name || "",
            total: dayOrders.reduce((sum, item) => sum + item.quantity, 0),
            totalOrders: dayOrders.length,
            dateFull: date,
          };
          this.mealCategories.forEach((restaurantName) => {
            dailyOrders[restaurantName] = dayOrders
              .filter((item) => item.restaurant_name === restaurantName)
              .map((meal) => ({
                label: `${meal.quantity}x ${meal.meal_name}`,
                quantity: meal.quantity,
                name: meal.meal_name,
                id: meal.meal_id,
              }));
          });
          return dailyOrders;
        });
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    employee: {
      handler(val) {
        if (val) this.loadReport();
      },
      immediate: true,
    },
    date: {
      handler() {
        this.loadReport();
      },
    },
  },
};
</script>

<style scoped>
.v-data-table >>> tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.05) !important;
}
table tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.05) !important;
}
div.v-select__selections span.v-size--small span.v-chip__content {
  font-size: 10px !important;
}
</style>
